import { EventDto, EventFrequency, WeekdayEnum } from "dtos/events.dto";
import { RRule, Frequency, Weekday } from "rrule";

const FREQUENCY_MAP: Record<EventFrequency, Frequency> = {
  DAILY: RRule.DAILY,
  WEEKLY: RRule.WEEKLY,
  MONTHLY: RRule.MONTHLY,
  YEARLY: RRule.YEARLY,
};

const WEEKDAY_MAP: Record<WeekdayEnum, Weekday> = {
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
  SU: RRule.SU,
};

export function eventToRRule(event: EventDto): RRule | null {
  if (!event.repeat) {
    return null;
  }

  const { frequency, interval, byDay, byMonthDay, count, until } = event.repeat;

  const options: any = {
    freq: FREQUENCY_MAP[frequency],
    interval: interval || 1,
    dtstart: new Date(event.start_date),
  };

  if (byDay && byDay.length > 0) {
    options.byweekday = byDay.map((day) => WEEKDAY_MAP[day]);
  }

  if (byMonthDay && byMonthDay.length > 0) {
    options.bymonthday = byMonthDay;
  }

  if (count) {
    options.count = count;
  } else if (until) {
    options.until = new Date(until);
  }

  return new RRule(options);
}

export function getNextNOccurrences(event: EventDto, n: number, from?: Date): Date[] {
  const rrule = eventToRRule(event);
  if (!rrule) return [];

  const startFrom = from || new Date(event.start_date);
  const farFuture = new Date(startFrom.getTime() + 365 * 24 * 60 * 60 * 1000);

  //true means inclusive of start_date
  return rrule.between(startFrom, farFuture, true).slice(0, n);
}

export function getOccurrencesBetween(event: EventDto, startDate: Date, endDate: Date): Date[] {
  const rrule = eventToRRule(event);
  if (!rrule) return [];

  return rrule.between(startDate, endDate, true);
}

export function getAllOccurrences(event: EventDto, max: number = 100): Date[] {
  const rrule = eventToRRule(event);
  if (!rrule) return [];

  return rrule.all((date, i) => i < max);
}

export function getNextOccurrence(event: EventDto, after: Date = new Date()): Date | null {
  const rrule = eventToRRule(event);
  if (!rrule) return null;

  return rrule.after(after, true);
}

export function getPreviousOccurrence(event: EventDto, before: Date = new Date()): Date | null {
  const rrule = eventToRRule(event);
  if (!rrule) return null;

  const startDate = new Date(event.start_date);

  if (before <= startDate) {
    return null;
  }

  const prev = rrule.before(before, true);

  if (!prev || prev < startDate) {
    return null;
  }

  return prev;
}

export function getRecurrenceText(event: EventDto): string {
  const rrule = eventToRRule(event);
  if (!rrule) return "Does not repeat";

  return rrule.toText();
}
